/**
 * Service for loading visualization data based on workflow configuration.
 *
 * This service handles the data loading layer:
 * - Downloads data based on workflow slot configuration
 * - Parses based on data type (VOLUME/LABEL_MAP → vtkImageData, JSON → object)
 * - Emits progress events for UI updates
 * - No domain knowledge - returns raw data only
 */
import type { ICaseDataResponse, TDataType } from '@escloud/types';
import type vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';

import { PubSubService } from '../core/pubsub-service';
import type { ServiceContext, ServiceRegistration } from '../core/types';
import { parseMha } from '../utils/mha-parser';

// ============================================================================
// Types
// ============================================================================

/**
 * Status of a single data slot.
 */
export type SlotLoadStatus = 'idle' | 'loading' | 'loaded' | 'error' | 'not_found';

/**
 * Raw loaded data for a single slot.
 * No domain transformation - just raw vtkImageData or parsed JSON.
 */
export interface LoadedSlotData {
    /** Data slot key */
    readonly key: string;
    /** Data type from API */
    readonly type: TDataType;
    /** Current loading status */
    readonly status: SlotLoadStatus;
    /** Raw loaded data: vtkImageData for volumes, object for JSON */
    readonly data?: vtkImageData | unknown;
    /** Metadata from API (e.g., label map structures) */
    readonly metadata?: Record<string, unknown>;
    /** Error message if status is 'error' */
    readonly error?: string;
}

/**
 * Event payload for LOADING_STARTED.
 */
export interface LoadingStartedPayload {
    caseId: string;
    workflowId: string;
    slots: string[];
}

/**
 * Event payload for SLOT_LOADING.
 */
export interface SlotLoadingPayload {
    key: string;
}

/**
 * Event payload for SLOT_LOADED.
 */
export interface SlotLoadedPayload {
    key: string;
    type: TDataType;
    data: vtkImageData | unknown;
    metadata?: Record<string, unknown>;
}

/**
 * Event payload for SLOT_ERROR.
 */
export interface SlotErrorPayload {
    key: string;
    error: string;
}

/**
 * Event payload for SLOT_NOT_FOUND.
 */
export interface SlotNotFoundPayload {
    key: string;
}

/**
 * Event payload for ALL_COMPLETE.
 */
export interface AllCompletePayload {
    loaded: string[];
    failed: string[];
    notFound: string[];
}

/**
 * Interface for data service dependency.
 */
export interface IDataService {
    listData(caseId: string, key?: string, signal?: AbortSignal): Promise<ICaseDataResponse[]>;
    downloadData(caseId: string, dataId: string, hashSum: string, signal?: AbortSignal): Promise<ArrayBuffer>;
}

/**
 * Configuration for DataLoaderService.
 */
export interface DataLoaderServiceConfig {
    dataService: IDataService;
    [key: string]: unknown;
}

// ============================================================================
// DataLoaderService
// ============================================================================

/**
 * Service for loading raw visualization data based on workflow configuration.
 *
 * Returns raw data (vtkImageData, JSON objects) - no domain transformation.
 * Domain transformation is handled by workflow-specific services.
 *
 * @example
 * ```typescript
 * const loader = serviceManager.getService<DataLoaderService>(DataLoaderService.SERVICE_NAME);
 *
 * // Subscribe to events
 * loader.subscribe(loader.EVENTS.SLOT_LOADED, ({ key, type, data, metadata }) => {
 *     // data is vtkImageData for VOLUME/LABEL_MAP, or raw object for JSON
 * });
 *
 * // Start loading
 * await loader.loadForCase(caseId, workflowId);
 *
 * // Access raw loaded data
 * const ctData = loader.getSlotData('ct-angio');
 * if (ctData?.type === 'VOLUME') {
 *     const imageData = ctData.data as vtkImageData;
 * }
 * ```
 */
export class DataLoaderService extends PubSubService {
    static readonly SERVICE_NAME = 'DataLoaderService';

    static readonly REGISTRATION: ServiceRegistration = {
        name: DataLoaderService.SERVICE_NAME,
        factory: (context: ServiceContext) => new DataLoaderService(context),
    };

    readonly EVENTS = {
        /** Emitted when loading starts for a case */
        LOADING_STARTED: 'dataloader:loading:started',
        /** Emitted when a slot begins loading */
        SLOT_LOADING: 'dataloader:slot:loading',
        /** Emitted when a slot finishes loading successfully */
        SLOT_LOADED: 'dataloader:slot:loaded',
        /** Emitted when a slot fails to load */
        SLOT_ERROR: 'dataloader:slot:error',
        /** Emitted when a slot is not found */
        SLOT_NOT_FOUND: 'dataloader:slot:not_found',
        /** Emitted when all slots have been processed */
        ALL_COMPLETE: 'dataloader:all:complete',
    } as const;

    private dataService: IDataService;
    private slots: Map<string, LoadedSlotData> = new Map();
    private currentCaseId: string | null = null;
    private currentWorkflowId: string | null = null;
    private isLoading = false;

    constructor(context: ServiceContext) {
        super();
        const config = context.config as DataLoaderServiceConfig | undefined;
        if (!config?.dataService) {
            throw new Error('DataLoaderService requires dataService in config');
        }
        this.dataService = config.dataService;
    }

    /**
     * Load all data slots for a case based on its workflow.
     *
     * @param caseId - The case ID
     * @param workflowId - The workflow ID (determines which slots to load)
     * @param slotKeys - The list of case data slots to load
     */
    async loadForCase(
        caseId: string,
        workflowId: string,
        slotKeys: string[],
        signal?: AbortSignal
    ): Promise<void> {
        if (slotKeys.length === 0) {
            console.warn(`No data slots configured for workflow: ${workflowId}`);
        }

        // Reset state for new case
        this.slots.clear();
        this.currentCaseId = caseId;
        this.currentWorkflowId = workflowId;
        this.isLoading = true;

        // Initialize slot states
        for (const key of slotKeys) {
            this.slots.set(key, {
                key,
                type: 'VOLUME', // Will be updated when we fetch metadata
                status: 'idle',
            });
        }

        // Emit loading started
        this.broadcast(this.EVENTS.LOADING_STARTED, {
            caseId,
            workflowId,
            slots: slotKeys,
        } satisfies LoadingStartedPayload);

        // Load each slot sequentially for better progress visibility
        const loaded: string[] = [];
        const failed: string[] = [];
        const notFound: string[] = [];

        for (const key of slotKeys) {
            signal?.throwIfAborted();
            try {
                const result = await this.loadSlot(caseId, key, signal);
                if (result === 'loaded') {
                    loaded.push(key);
                } else if (result === 'not_found') {
                    notFound.push(key);
                }
            } catch {
                failed.push(key);
            }
        }

        this.isLoading = false;

        // Emit all complete
        this.broadcast(this.EVENTS.ALL_COMPLETE, {
            loaded,
            failed,
            notFound,
        } satisfies AllCompletePayload);
    }

    /**
     * Load a single data slot.
     */
    private async loadSlot(
        caseId: string,
        key: string,
        signal?: AbortSignal
    ): Promise<'loaded' | 'not_found'> {
        // Emit loading started for this slot
        this.broadcast(this.EVENTS.SLOT_LOADING, { key } satisfies SlotLoadingPayload);

        this.updateSlotStatus(key, 'loading');

        try {
            // Get metadata from API
            const dataItems = await this.dataService.listData(caseId, key, signal);

            if (dataItems.length === 0) {
                this.updateSlotStatus(key, 'not_found');
                this.broadcast(this.EVENTS.SLOT_NOT_FOUND, { key } satisfies SlotNotFoundPayload);
                return 'not_found';
            }

            const dataInfo = dataItems[0];
            const dataType = dataInfo.type as TDataType;

            // Load raw data based on type
            const data = await this.loadByType(caseId, dataInfo, signal);

            // Update slot with loaded data
            this.slots.set(key, {
                key,
                type: dataType,
                status: 'loaded',
                data,
                metadata: dataInfo.metadata,
            });

            // Emit loaded event
            this.broadcast(this.EVENTS.SLOT_LOADED, {
                key,
                type: dataType,
                data,
                metadata: dataInfo.metadata,
            } satisfies SlotLoadedPayload);

            return 'loaded';
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            this.slots.set(key, {
                ...this.slots.get(key)!,
                status: 'error',
                error: errorMessage,
            });

            this.broadcast(this.EVENTS.SLOT_ERROR, {
                key,
                error: errorMessage,
            } satisfies SlotErrorPayload);

            throw error;
        }
    }

    /**
     * Load raw data based on its type.
     * Returns vtkImageData for volumes, raw object for JSON.
     */
    private async loadByType(
        caseId: string,
        dataInfo: ICaseDataResponse,
        signal?: AbortSignal
    ): Promise<vtkImageData | unknown> {
        const dataType = dataInfo.type as TDataType;

        switch (dataType) {
            case 'VOLUME':
            case 'LABEL_MAP':
                return this.loadVolumeData(caseId, dataInfo, signal);

            case 'JSON':
                return this.loadJsonData(caseId, dataInfo, signal);

            default:
                console.warn(`Unsupported data type: ${dataType}, loading as raw ArrayBuffer`);
                return this.dataService.downloadData(
                    caseId,
                    dataInfo.id,
                    dataInfo.file?.sha256 ?? '',
                    signal
                );
        }
    }

    /**
     * Load volume data (MHA file) as vtkImageData.
     */
    private async loadVolumeData(
        caseId: string,
        dataInfo: ICaseDataResponse,
        signal?: AbortSignal
    ): Promise<vtkImageData> {
        const arrayBuffer = await this.dataService.downloadData(
            caseId,
            dataInfo.id,
            dataInfo.file?.sha256 ?? '',
            signal
        );
        return parseMha(arrayBuffer);
    }

    /**
     * Load JSON data as raw object.
     */
    private async loadJsonData(
        caseId: string,
        dataInfo: ICaseDataResponse,
        signal?: AbortSignal
    ): Promise<unknown> {
        const arrayBuffer = await this.dataService.downloadData(
            caseId,
            dataInfo.id,
            dataInfo.file?.sha256 ?? '',
            signal
        );
        const text = new TextDecoder().decode(arrayBuffer);
        return JSON.parse(text);
    }

    /**
     * Update slot status helper.
     */
    private updateSlotStatus(key: string, status: SlotLoadStatus): void {
        const slot = this.slots.get(key);
        if (slot) {
            this.slots.set(key, { ...slot, status });
        }
    }

    // ========================================================================
    // Public Accessors
    // ========================================================================

    /**
     * Get current loading state.
     */
    getIsLoading(): boolean {
        return this.isLoading;
    }

    /**
     * Get current case ID.
     */
    getCurrentCaseId(): string | null {
        return this.currentCaseId;
    }

    /**
     * Get current workflow ID.
     */
    getCurrentWorkflowId(): string | null {
        return this.currentWorkflowId;
    }

    /**
     * Get all loaded slots.
     */
    getAllSlots(): Map<string, LoadedSlotData> {
        return new Map(this.slots);
    }

    /**
     * Get a specific slot's data.
     */
    getSlotData(key: string): LoadedSlotData | undefined {
        return this.slots.get(key);
    }

    /**
     * Get raw vtkImageData for a volume slot.
     */
    getVolumeData(key: string): vtkImageData | undefined {
        const slot = this.slots.get(key);
        if (slot?.status === 'loaded' && (slot.type === 'VOLUME' || slot.type === 'LABEL_MAP')) {
            return slot.data as vtkImageData;
        }
        return undefined;
    }

    /**
     * Get raw JSON data for a JSON slot.
     */
    getJsonData<T = unknown>(key: string): T | undefined {
        const slot = this.slots.get(key);
        if (slot?.status === 'loaded' && slot.type === 'JSON') {
            return slot.data as T;
        }
        return undefined;
    }

    /**
     * Get metadata for a slot (e.g., label map structures).
     */
    getSlotMetadata(key: string): Record<string, unknown> | undefined {
        return this.slots.get(key)?.metadata;
    }

    /**
     * Check if all configured slots are loaded or have a terminal status.
     */
    isComplete(): boolean {
        for (const slot of this.slots.values()) {
            if (slot.status === 'idle' || slot.status === 'loading') {
                return false;
            }
        }
        return true;
    }

    /**
     * Load a data item by key from the data service.
     * Used by workflow services to load data items not covered by slots (e.g., meshes).
     *
     * @param caseId - Case identifier
     * @param key - Data key to search for
     * @param signal - Optional AbortSignal for cancellation
     * @returns ArrayBuffer of the first matching data item, or null if not found
     */
    async loadDataByKey(caseId: string, key: string, signal?: AbortSignal): Promise<ArrayBuffer | null> {
        const dataItems = await this.dataService.listData(caseId, key, signal);
        if (dataItems.length === 0) return null;
        return this.dataService.downloadData(
            caseId,
            dataItems[0].id,
            dataItems[0].file?.sha256 ?? '',
            signal
        );
    }

    /**
     * Check if any slot has an error.
     */
    hasErrors(): boolean {
        for (const slot of this.slots.values()) {
            if (slot.status === 'error') {
                return true;
            }
        }
        return false;
    }

    /**
     * Reset the service state.
     */
    override reset(): void {
        console.log(`${DataLoaderService.SERVICE_NAME} reset called`);
        super.reset();
        this.slots.clear();
        this.currentCaseId = null;
        this.currentWorkflowId = null;
        this.isLoading = false;
    }
}
